import camelcase from 'camelcase';
import classNames from 'classnames';
import {
  add,
  endOfDay,
  isEqual,
  startOfDay,
  startOfToday,
} from 'date-fns';
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Field, useForm, useFormState,
} from 'react-final-form';
import { TripCartFormState } from 'src/listing/types';
import {
  getDurationDigits,
} from 'src/listing/helper';
import BookingMotivator from 'src/common/components/BookingMotivator';
import Datepicker from 'src/common/components/Datepicker';
import {
  formatDate, todayDate,
} from 'src/common/utils/dateHelpers';
import getClassNames from 'src/common/utils/getClassNames';
import { FORM_SELECTED_DATES } from 'src/listing/constants';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import TripCartDuration from 'src/trip-cart/components/TripCartDuration';
import { useTripCartContext } from 'src/trip-cart/hooks/useTripCartContext';
import { useTripCartOptionsContext } from 'src/trip-cart/hooks/useTripCartOptionsContext';
import DayContent from 'src/common/components/DayContent/DayContent';
import dayContentStyles from 'src/common/components/DayContent/DayContent.module.scss';
import { useListingMotivators } from 'src/listing/hooks/useListingPageMotivators';
import { useTripCartSeasonsContext } from 'src/trip-cart/hooks/useTripCartSeasonsContext';
import { BOOKING_MOTIVATOR_STATES } from 'src/common/constants';
import debounce from 'lodash.debounce';
import { getCachedSeasonForDate } from 'src/trip-cart/utils/tripCartCache';
import styles from './TripDetailsCalendar.module.scss';

const DEBOUNCE_MS = 300;

export type Props = {
  inSheet?: boolean;
};

const TripCartCalendar: FC<Props> = ({
  inSheet = false,
}) => {
  const { setMotivatorsDates } = useListingMotivators();
  const {
    bookingMotivators,
    getSelectedMotivators,
    getMultiSelectedMotivators,
    changeVisibleCalendarMonth,
  } = useBookingMotivatorsContext();

  const {
    listing,
  } = useTripCartContext();

  const {
    blockedTimes,
    blockedTimesLoading,
    options,
    optionsLoading,
  } = useTripCartOptionsContext();

  const { hasDayTierPrices } = useTripCartSeasonsContext();

  const minDate = startOfToday();
  const form = useForm<TripCartFormState>();
  const { change } = form;
  const {
    values: {
      formSelectedDates,
      isMultiDay,
      formDuration,
    },
  } = useFormState<TripCartFormState>();

  const [selected, setSelected] = useState<Date | Date[]>(formSelectedDates);
  const start = Array.isArray(selected) ? selected[0] : selected;
  const end = selected?.[1];

  const selectedMotivators = useMemo(() => {
    if (isMultiDay) {
      return getMultiSelectedMotivators(formSelectedDates);
    }
    return getSelectedMotivators(formSelectedDates);
  }, [formSelectedDates, getMultiSelectedMotivators, getSelectedMotivators, isMultiDay]);

  const showBookingIndicators = (date: Date) => {
    const motivators = isMultiDay
      ? bookingMotivators[formatDate(date)]
        ?.filter(
          (motivator) => motivator !== BOOKING_MOTIVATOR_STATES.NO_SAME_DAY_PRICING,
        ) ?? []
      : bookingMotivators[formatDate(date)] ?? [];

    return classNames(
      dayContentStyles.indicator,
      motivators.map((motivator) => dayContentStyles[camelcase(motivator)]),
    );
  };

  const changeSelectedOnForm = useCallback(async (
    date: Date | Date[] | null,
  ) => {
    if (isMultiDay || !date) {
      return;
    }
    if (Array.isArray(date)) {
      change(FORM_SELECTED_DATES, date);
      setMotivatorsDates(date);
      return;
    }

    change(FORM_SELECTED_DATES, date ? [date] : []);
    setMotivatorsDates(date ? [date] : []);
  }, [change, isMultiDay, setMotivatorsDates]);

  // For Multi-Day/Night
  const maxDays = useMemo(() => getDurationDigits(formDuration), [formDuration]);

  const changeSelectedRangeOnForm = useCallback(async (selectedRange: [Date, Date]) => {
    change(FORM_SELECTED_DATES, selectedRange);
    setMotivatorsDates(selectedRange);
  }, [change, setMotivatorsDates]);

  const changeMonth = (date: Date) => {
    changeVisibleCalendarMonth(date);
  };

  const changeSelected = useCallback((
    date: Date | Date[] | null,
    allowFormUpdate: boolean = true,
  ) => {
    if (isMultiDay || !date) return;
    const startDate = Array.isArray(date) ? date[0] : date;
    setSelected(startDate);

    if (allowFormUpdate) {
      changeSelectedOnForm(startDate);
    }
  }, [changeSelectedOnForm, isMultiDay]);

  const changeSelectedRange = useCallback((date: Date, allowFormUpdate: boolean = true) => {
    if (!isMultiDay || !date) return;

    const range = [date, add(date, { days: hasDayTierPrices ? maxDays - 1 : maxDays })];

    // Ensure that we include info from the start of the day
    range[0] = startOfDay(range[0]);

    if (range[1]) {
      // Ensure that we include info until the end of the day
      range[1] = endOfDay(range[1]);
    }
    setSelected(range);
    if (allowFormUpdate) {
      changeSelectedRangeOnForm([range[0], range[1]]);
    }
  }, [changeSelectedRangeOnForm, hasDayTierPrices, isMultiDay, maxDays]);

  /**
   * Debounce the changeSelected and changeSelectedRange functions
   * to prevent multiple calls within the debounce window.
   * Only debounce if data is not cached (server call needed).
   */
  const debouncedChangeSelected = useMemo(
    () => debounce(changeSelected, DEBOUNCE_MS),
    [changeSelected],
  );
  const debouncedChangeSelectedRange = useMemo(
    () => debounce(changeSelectedRange, DEBOUNCE_MS),
    [changeSelectedRange],
  );

  // Wrapper functions that check cache before deciding whether to debounce
  const smartChangeSelected = useCallback((
    date: Date | Date[] | null,
  ) => {
    if (isMultiDay || !date) {
      changeSelected(date);
      return;
    }
    const startDate = Array.isArray(date) ? date[0] : date;
    const cache = getCachedSeasonForDate({ date: formatDate(startDate) });
    if (cache) {
      changeSelected(date);
    } else {
      debouncedChangeSelected(date);
    }
  }, [changeSelected, debouncedChangeSelected, isMultiDay]);

  const smartChangeSelectedRange = useCallback((date: Date) => {
    if (!isMultiDay || !date) {
      changeSelectedRange(date);
      return;
    }
    const cache = getCachedSeasonForDate({ date: formatDate(date) });
    if (cache) {
      changeSelectedRange(date);
    } else {
      debouncedChangeSelectedRange(date);
    }
  }, [changeSelectedRange, debouncedChangeSelectedRange, isMultiDay]);

  // For external updates
  const [previousMaxDays, setPreviousMaxDays] = useState(maxDays);

  // Update from external changes
  useEffect(
    () => {
      // Prevent early changes
      if (optionsLoading) return;

      const changedDate = Array.isArray(formSelectedDates)
        ? formSelectedDates[0]
        : formSelectedDates;

      if (
        // if range not changed
        maxDays === previousMaxDays
        // date not changed
        && isEqual(startOfDay(start), startOfDay(changedDate))
        // hasn't changed from "with end date" to "no end date" or vice-versa
        && !!end === !!formSelectedDates?.[1]) {
        return;
      }
      setPreviousMaxDays(maxDays);
      if (isMultiDay) {
        changeSelectedRange(formSelectedDates[0], false);
      } else {
        changeSelected(formSelectedDates[0], false);
      }
    },
    [
      isMultiDay,
      end,
      formSelectedDates,
      changeSelected,
      changeSelectedRange,
      start,
      maxDays,
      previousMaxDays,
      optionsLoading,
    ],
  );

  // Can choose same day or multiday
  const isMixedOrMultiDayPricing = options.isMultiday.length > 1 || isMultiDay;

  return (
    <div className={styles.root}>
      <div className={styles.calendarWrapper}>
        <div className={styles.multiDayWrapper}>
          {isMultiDay && (
            <div className={styles.duration}>
              <TripCartDuration
                hideLabel
              />
            </div>
          )}
          {inSheet && (
            <div className={getClassNames(styles, 'duration', classNames({ release1FlagEnabled: inSheet }))}>
              <TripCartDuration
                inSheet
                showMultidayToggle={false}
              />
            </div>
          )}
        </div>
        <div className={getClassNames(styles, 'calendar', classNames({ isMixedOrMultiDayPricing }))}>
          <Field name={FORM_SELECTED_DATES}>
            {() => (
              <Datepicker
                id="trip-details-calendar"
                startDate={start}
                value={start}
                endDate={end}
                inline
                focusSelectedMonth
                minDate={minDate}
                maxDate={add(todayDate(), { years: 1 })}
                monthsShown={1}
                noBorder
                classNameModifier="listingTripDetails"
                onMonthChange={changeMonth}
                onChange={smartChangeSelected}
                selectsRange={isMultiDay}
                onSelect={smartChangeSelectedRange}
                dayClassName={showBookingIndicators}
                renderDayContents={(day, date) => (
                  <DayContent
                    bookingMotivators={bookingMotivators}
                    date={date}
                    day={day}
                  />
                )}
              />
            )}
          </Field>
        </div>
        <div className={styles.bookingMotivators}>
          <BookingMotivator
            variant="compact"
            motivators={selectedMotivators}
            blockedTimes={blockedTimes}
            blockedTimesLoading={blockedTimesLoading}
            dateCount={formSelectedDates.length}
            anticipatedDays={listing.cancellation_anticipation}
          />
        </div>
      </div>
    </div>
  );
};

export default TripCartCalendar;
